/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { AgentSettingsService } from '@src/app/pages/agent-settings/agent-settings.service';
import { maxChars, required } from '@src/app/shared/validators';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { MatCardModule } from '@angular/material/card';
import { TextEditComponent } from '@src/app/shared/simpleFields/text-edit/text-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { EditFieldDialogComponent, EditFieldDialogOptions } from '@src/app/shared/dialogs/edit-field-dialog/edit-field-dialog.component';
import { Field } from '@src/app/shared/field/Field';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { AgentSettings } from '@src/app/model/AgentSettings';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { FormEmailComponent } from '@src/app/shared/form/form-email/form-email.component';
import { AI_LEVEL_HINT, AI_LEVELS } from '@src/app/model/ai-level';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { DocHelpComponent } from '@src/app/shared/doc-help/doc-help.component';

@Component({
    selector: 'app-agent-settings',
    imports: [MatCardModule, TextEditComponent, DocHelpComponent],
    templateUrl: './agent-settings.component.html',
    styleUrl: './agent-settings.component.scss'
})
export class AgentSettingsComponent {
    /** If you are editing here, you likely need to make same change in OmcSettingsComponent */
    agentSettingsSvc = inject(AgentSettingsService);
    dialog = inject(MatDialog);
    cus = inject(CurrentUserService);
    cds = inject(ConfirmDialogService);


    team = new AgentSettings();
    loaded = false;

    constructor() {
        this.agentSettingsSvc.getOne('agentSettings', null).subscribe(team => {
            if (team) {
                this.team = team
            }
            this.loaded = true;
        });
    }

    deleteParameter(name: string, value: string) {
        if (!this.getParameter(name)) {
            this.cds.alert(
                $localize`Cannot remove parameter`,
                $localize`${value} is the default value for ${name}, you cannot remove the parameter, you can edit to override it`,
            );
        } else {
            this.cds.open(
                $localize`Remove Setting`,
                $localize`Do you wish to remove the '${name}' setting and revert to the default value`,
                () => this.agentSettingsSvc.deleteParameter(name, value, this.team).subscribe(x => this.team = x)
            );
        }
    }

    getParameter(name: string) {
        return this.team.parameters.find(p => p.name === name);
    }

    editName() {
        this.editField(FieldMaker.nameControl({ validators: [required] }));
    }

    editAddress() {
        this.editField(FormTextAreaComponent.make('Edit Business Address', 'defaultAddress', {
            validators: [required, maxChars(255)],
        }))
    }

    editMailSendingName() {
        const field = FormTextComponent.make('The name to send mails as', 'mailSendingName',
            {
                validators: [required],
                hint: 'Usually you can leave the default value here, and let the system use the name of the OMC, but enter a value here to override it'
            })
        this.editField(field);
    }

    editMailSendingAccount() {
        const field = FormTextComponent.make('The email account to send mail', 'mailSendingAccount',
            {
                validators: [required],
                hint: 'Usually you can leave the default value here, and let the system use the ID of the OMC, the account used here must exist in your sending domain'
            })
        this.editField(field);
    }

    editReplyToMail() {
        const field = FormEmailComponent.make('Email Address if recipient presses reply', 'replyToMail',
            {
                validators: [required],
                hint: `Note: It can reduce your mails deliverability if the replyTo does not match the sender email address,
                especially if the address you use is a free account, e.g. gmail etc. Unless you really know what you are doing
                it is usually best to leave the replyToMail alone. If reply comes back to OurOMC we will create a request for you.
                `
            })
        this.editField(field);
    }

    editBillingAddress() {
        this.editField(FormTextAreaComponent.make('Billing Address', 'billingAddress', {
            validators: [required, maxChars(255)],
            hint: 'This will be the default billing address for all of your OMCs unless you change the settings on the OMC itself'
        }))
    }


    editMailSendingDomain() {
        this.editField(FormTextComponent.make('Mail Sending Domain', 'mailSendingDomain', {
            hint: 'Must be configured by OurOMC',
            disable: !this.cus.hasUber(),
            validators: [required, maxChars(255)],
        }))
    }

    editMailReplyToDomain() {
        this.editField(FormTextComponent.make('Mail Reply To Domain', 'mailReplyToDomain', {
            hint: 'Must be configured by OurOMC',
            disable: !this.cus.hasUber(),
            validators: [required, maxChars(255)],
        }))
    }

    editLegalName() {
        const field = FormTextComponent.make('Legal Company Name', 'legalName', { validators: [required] })
        this.editField(field);
    }

    editUseLogo() {
        this.editField(FormPicklistComponent.make('Use Logo', 'useLogo', null, {
            items: [{id: 'yes', name: 'Yes'}, {id: 'no', name: 'No'}]
        }));
    }

    editAiLevel() {
        this.editField(FormPicklistComponent.make('AI Level', 'aiLevel', null, {
            items: AI_LEVELS,
        }, { hint: AI_LEVEL_HINT }));
    }

    editShowOmcDashboard() {
        this.editField(FormPicklistComponent.make('Show OMC Dashboard', 'showOmcDashboard', null, {
            items: [{id: 'no', name: 'No'}, {id: 'directors', name: 'Directors'}]
        }));
    }

    editPaymentTerms() {
        this.editField(FormTextAreaComponent.make('Default Payment Terms', 'paymentTerms',
            { validators: [required, maxChars(255)],
              hint: 'This will be the default billing address for all of your OMCs unless you change the settings on the OMC itself'
            }
        ));
    }

    editServiceChargeLabel() {
        this.editField(FormTextComponent.make('Default Service Charge Label', 'serviceChargeLabel', {
            validators: [required, maxChars(60)],
            hint: 'What to call a Service Charge on PDF requests for payment and statements. Defaults to "Invoice Notice", a middle ground that reads as an invoice for OCR/approved-housing-body purposes without conceding it is one. Not used elsewhere in the app. This is the default for all of your OMCs unless overridden on the OMC itself'
        }));
    }

    editCreditNoteLabel() {
        this.editField(FormTextComponent.make('Default Credit Note Label', 'creditNoteLabel', {
            validators: [required, maxChars(60)],
            hint: 'What to call a Credit Note on PDF requests for payment and statements. Defaults to "Credit Notice". Not used elsewhere in the app. This is the default for all of your OMCs unless overridden on the OMC itself'
        }));
    }

    editField(field: Field) {
        const data = new EditFieldDialogOptions({
            field,
            id: 'agentSettings',
            service: this.agentSettingsSvc,
        });
        this.dialog.open(EditFieldDialogComponent,{data}).afterClosed().subscribe((updated) => {
            if (updated) {
                this.team = updated;
            }
        })
    }
}
